/**
 * Inclusão em lote de operações num grupo de plano de manutenção.
 *
 * **A planilha não vai ao disco.** O sistema antigo salva em `App_Data` antes de
 * abrir, porque o leitor dele exige um caminho; a API lê do próprio fluxo do
 * pedido, e por isso esta tela não espera o servidor de arquivos.
 *
 * **Confere antes, como as outras importações.** O `/conferir` roda a planilha
 * inteira sem gravar e devolve linha a linha o que entraria e o que seria
 * descartado, com o motivo.
 *
 * **A repetição passa a valer, e é diferença deliberada.** No sistema antigo ela
 * é lida com cuidado e logo abaixo vem um `||` onde devia ser `&&`, numa
 * condição sempre verdadeira que zera o valor — todo item importado nasce sem
 * repetição, qualquer que seja a planilha. A conferência mostra o que vai ser
 * gravado antes de gravar.
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto } from "@/api/cliente"
import { Aviso, Botao, Campo, Linha, mensagem } from "./pecas"

type ItemDoLote = {
  linha: number
  tempo: number
  km: number
  operacao: string
  pagina: string
  repeticao: boolean
  motivo: string
}

type ResultadoDoLote = {
  lidas: number
  entram: number
  descartadas: number
  criados: number
  avisos: string[]
  itens: ItemDoLote[]
}

/** Quantas linhas do relatório desenhar. */
const DESENHADAS = 60

export function LoteDoPlano({ grupoId, aoAvisar }: {
  grupoId: number
  aoAvisar: (m: string | null) => void
}) {
  const qc = useQueryClient()

  const [aberto, setAberto] = useState(false)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [motorId, setMotorId] = useState("")
  const [ano, setAno] = useState("")
  const [manual, setManual] = useState("")
  const [colunas, setColunas] = useState({ tempo: "1", km: "2", operacao: "3", pagina: "", repeticao: "" })
  const [previa, setPrevia] = useState<ResultadoDoLote | null>(null)

  const enviar = useMutation({
    mutationFn: async (acao: "conferir" | "aplicar") => {
      const corpo = new FormData()
      corpo.append("motorId", motorId || "0")
      corpo.append("ano", ano || "0")
      corpo.append("manual", manual)
      corpo.append("colunaTempo", colunas.tempo || "0")
      corpo.append("colunaKm", colunas.km || "0")
      corpo.append("colunaOperacao", colunas.operacao || "0")
      corpo.append("colunaPagina", colunas.pagina || "0")
      corpo.append("colunaRepeticao", colunas.repeticao || "0")
      corpo.append("arquivo", arquivo!)

      const r = await api.post<ResultadoDoLote>(
        `api/planos-manutencao/grupos/${grupoId}/itens/lote/${acao}`, corpo, foraDoProjeto())
      return r.data
    },
    onSuccess: (r, acao) => {
      setPrevia(r)
      if (acao === "aplicar") {
        qc.invalidateQueries({ queryKey: ["outros-filhos", "grupo-itens", grupoId] })
        aoAvisar(`${r.criados} operação(ões) criada(s).`)
      }
    },
    onError: (e) => { setPrevia(null); aoAvisar(mensagem(e)) },
  })

  if (!aberto) {
    return (
      <div style={{ marginTop: 10 }}>
        <Botao aoClicar={() => setAberto(true)}>Incluir em lote, de planilha…</Botao>
      </div>
    )
  }

  const pronto = arquivo != null && Number(motorId) > 0 && Number(ano) > 0

  return (
    <div style={{
      marginTop: 10, padding: 11, borderRadius: 6,
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <b style={{ fontSize: 12 }}>Incluir em lote</b>
        <button onClick={() => { setAberto(false); setPrevia(null) }} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 15, lineHeight: 1,
        }}>×</button>
      </div>

      <Aviso>
        <b>Motor, ano e manual valem para a planilha inteira.</b> O que varia linha a linha é
        tempo, km, operação, página e repetição — diga em que coluna cada um está, contando a
        partir de 1. A primeira linha é tratada como cabeçalho.
      </Aviso>

      <Linha>
        <Campo rotulo="Motor (id)">
          <input value={motorId} onChange={(e) => setMotorId(e.target.value)}
                 inputMode="numeric" style={{ ...entrada, width: 90 }} />
        </Campo>
        <Campo rotulo="Ano">
          <input value={ano} onChange={(e) => setAno(e.target.value)}
                 inputMode="numeric" style={{ ...entrada, width: 90 }} />
        </Campo>
        <Campo rotulo="Manual">
          <input value={manual} onChange={(e) => setManual(e.target.value)}
                 style={{ ...entrada, width: 180 }} />
        </Campo>
      </Linha>

      <Linha>
        {([
          ["tempo", "Tempo", true],
          ["km", "Km", true],
          ["operacao", "Operação", true],
          ["pagina", "Página", false],
          ["repeticao", "Repetição", false],
        ] as const).map(([chave, rotulo, obrigatorio]) => (
          <Campo key={chave} rotulo={`${rotulo}${obrigatorio ? "" : " (opcional)"}`}>
            <input
              value={colunas[chave]}
              onChange={(e) => setColunas((c) => ({ ...c, [chave]: e.target.value }))}
              inputMode="numeric"
              placeholder="col."
              style={{ ...entrada, width: 64 }}
            />
          </Campo>
        ))}
      </Linha>

      <div style={{ margin: "8px 0" }}>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); setPrevia(null) }}
          style={{ fontSize: 11.5 }}
        />
      </div>

      <Linha>
        <Botao
          aoClicar={() => { aoAvisar(null); enviar.mutate("conferir") }}
          desligado={!pronto}
          ocupado={enviar.isPending}
        >
          {enviar.isPending ? "Lendo…" : "Conferir a planilha"}
        </Botao>
        {previa && previa.entram > 0 && (
          <Botao
            destaque
            aoClicar={() => { aoAvisar(null); enviar.mutate("aplicar") }}
            ocupado={enviar.isPending}
          >
            Criar as {previa.entram}
          </Botao>
        )}
      </Linha>

      {previa && (
        <div style={{ marginTop: 9, fontSize: 11.5, lineHeight: 1.6 }}>
          <p style={{ margin: 0 }}>
            <b>{previa.lidas}</b> linha(s) lidas: <b>{previa.entram}</b> entram,{" "}
            {previa.descartadas} descartadas
            {previa.criados > 0 && <> · <b style={{ color: "var(--ok)" }}>{previa.criados} criadas</b></>}
          </p>
          {previa.avisos.map((a, i) => (
            <p key={i} style={{ margin: "5px 0 0", color: "var(--aviso-on-soft, #7a4b00)" }}>{a}</p>
          ))}

          {previa.itens.length > 0 && (
            <div style={{
              maxHeight: 210, overflowY: "auto", marginTop: 7,
              border: "1px solid var(--line2)", borderRadius: 5, background: "var(--surface)",
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <tbody>
                  {previa.itens.slice(0, DESENHADAS).map((i) => (
                    <tr key={i.linha} style={{ borderBottom: "1px solid var(--line2)" }}>
                      <td style={{ ...cel, width: 42, color: "var(--txt3)" }}>{i.linha}</td>
                      {i.motivo ? (
                        <td style={{ ...cel, color: "var(--txt3)" }} colSpan={4}>{i.motivo}</td>
                      ) : (
                        <>
                          <td style={{ ...cel, width: 62 }} className="num">{i.tempo} m</td>
                          <td style={{ ...cel, width: 82 }} className="num">
                            {i.km.toLocaleString("pt-BR")}
                          </td>
                          <td style={cel} title={i.operacao}>
                            <span style={{
                              display: "block", overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>{i.operacao}</span>
                          </td>
                          <td style={{ ...cel, width: 74, color: "var(--txt3)" }}>
                            {i.repeticao ? "repete" : ""}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {previa.itens.length > DESENHADAS && (
                <p style={{ margin: 0, padding: "5px 8px", fontSize: 10.5, color: "var(--txt3)" }}>
                  {previa.itens.length.toLocaleString("pt-BR")} linhas; mostrando {DESENHADAS}.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const cel: React.CSSProperties = { padding: "3px 7px", verticalAlign: "top" }
const entrada: React.CSSProperties = {
  padding: "4px 7px", fontSize: 11.5,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}
